import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Train, 
  MapPin, 
  Camera, 
  Search,
  Map as MapIcon,
  List,
  Filter,
  Radio
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DepotMap from "@/components/DepotMap";
import { BrandChip } from "@/components/BrandChip";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// State name mapping
const STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
  MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
  OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming"
};

export default function StateDepots() {
  const { stateCode } = useParams();
  const navigate = useNavigate();
  const [depots, setDepots] = useState([]);
  const [towers, setTowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showMap, setShowMap] = useState(true);
  const [showDepots, setShowDepots] = useState(true);
  const [showTowers, setShowTowers] = useState(true);

  const stateName = STATE_NAMES[stateCode?.toUpperCase()] || stateCode;

  const fetchDepots = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("state", stateCode);
      if (searchQuery) params.append("search", searchQuery);
      if (statusFilter !== "all") params.append("status", statusFilter);
      params.append("limit", "5000"); // ensure we return every depot in the state

      const towerParams = new URLSearchParams();
      towerParams.append("state", stateCode);
      if (searchQuery) towerParams.append("search", searchQuery);
      towerParams.append("limit", "2000");

      const [depotsRes, towersRes] = await Promise.all([
        axios.get(`${API}/depots?${params.toString()}`),
        axios.get(`${API}/towers?${towerParams.toString()}`),
      ]);
      setDepots(depotsRes.data);
      setTowers(towersRes.data);
    } catch (error) {
      console.error("Error fetching depots:", error);
    } finally {
      setLoading(false);
    }
  }, [stateCode, searchQuery, statusFilter]);

  useEffect(() => {
    fetchDepots();
  }, [fetchDepots]);

  const handleDepotClick = (depot) => {
    navigate(`/depot/${depot.id}`);
  };

  const handleTowerClick = (tower) => {
    navigate(`/tower/${tower.id}`);
  };

  const originalCount = depots.filter(d => d.status === "original").length;
  const relocatedCount = depots.filter(d => d.status === "relocated").length;
  const withPhotosCount = depots.filter(d => d.has_photo).length;

  return (
    <div className="min-h-screen bg-[#0a0a0a] page-transition">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/states")}
              className="text-white hover:bg-white/5"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-serif font-bold text-lg text-white">{stateName}</h1>
              <p className="text-sm text-white/50">
                {depots.length} depots
                {towers.length > 0 && <span className="text-white/30"> · {towers.length} towers</span>}
              </p>
            </div>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 flex items-center justify-center">
                <BrandChip size={36} to={null} />
              </div>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-[#111] border-white/5">
            <CardContent className="p-5 text-center">
              <p className="font-mono text-3xl font-bold stat-number">{depots.length}</p>
              <p className="text-sm text-white/40 mt-1">Total Depots</p>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-white/5">
            <CardContent className="p-5 text-center">
              <p className="font-mono text-3xl font-bold text-emerald-400">{originalCount}</p>
              <p className="text-sm text-white/40 mt-1">Original Site</p>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-white/5">
            <CardContent className="p-5 text-center">
              <p className="font-mono text-3xl font-bold text-blue-400">{relocatedCount}</p>
              <p className="text-sm text-white/40 mt-1">Relocated</p>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-white/5">
            <CardContent className="p-5 text-center">
              <p className="font-mono text-3xl font-bold text-amber-500">{withPhotosCount}</p>
              <p className="text-sm text-white/40 mt-1">With Photos</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
            <Input
              type="text"
              placeholder="Search depots in this state..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-amber-500/50"
              data-testid="depot-search-input"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-[160px] h-12 bg-white/5 border-white/10 text-white" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-white/10">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="original">Original Site</SelectItem>
              <SelectItem value="relocated">Relocated</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex gap-2">
            <Button
              variant={showMap ? "default" : "outline"}
              size="icon"
              className={`h-12 w-12 ${showMap ? "bg-amber-500 text-black hover:bg-amber-600" : "border-white/20 text-white hover:bg-white/5"}`}
              onClick={() => setShowMap(true)}
              data-testid="view-map-btn"
            >
              <MapIcon className="w-5 h-5" />
            </Button>
            <Button
              variant={!showMap ? "default" : "outline"}
              size="icon"
              className={`h-12 w-12 ${!showMap ? "bg-amber-500 text-black hover:bg-amber-600" : "border-white/20 text-white hover:bg-white/5"}`}
              onClick={() => setShowMap(false)}
              data-testid="view-list-btn"
            >
              <List className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/50" />
            <span className="text-white/60">Original (with photo)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500 shadow-lg shadow-red-500/50" />
            <span className="text-white/60">Original (no photo)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500 shadow-lg shadow-blue-500/50" />
            <span className="text-white/60">Relocated (with photo)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500 shadow-lg shadow-yellow-500/50" />
            <span className="text-white/60">Relocated (no photo)</span>
          </div>
          {towers.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rotate-45 bg-amber-500 shadow-lg shadow-amber-500/50" />
              <span className="text-white/60">Tower</span>
            </div>
          )}

          <div className="ml-auto flex items-center gap-5">
            <div className="flex items-center gap-2">
              <Switch
                id="state-toggle-depots"
                checked={showDepots}
                onCheckedChange={setShowDepots}
                data-testid="toggle-depots"
              />
              <Label htmlFor="state-toggle-depots" className="text-white/70 cursor-pointer text-sm">
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> Depots
                </span>
              </Label>
            </div>
            {towers.length > 0 && (
              <div className="flex items-center gap-2">
                <Switch
                  id="state-toggle-towers"
                  checked={showTowers}
                  onCheckedChange={setShowTowers}
                  data-testid="toggle-towers"
                />
                <Label htmlFor="state-toggle-towers" className="text-white/70 cursor-pointer text-sm">
                  <span className="inline-flex items-center gap-1">
                    <Radio className="w-3.5 h-3.5" /> Towers
                  </span>
                </Label>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {showMap ? (
          <Card className="overflow-hidden bg-[#111] border-white/5" data-testid="map-card">
            <CardContent className="p-0">
              <div className="h-[550px] md:h-[650px]">
                <DepotMap 
                  depots={depots}
                  towers={towers}
                  onDepotClick={handleDepotClick}
                  onTowerClick={handleTowerClick}
                  loading={loading}
                  darkMode={true}
                  showDepots={showDepots}
                  showTowers={showTowers}
                  autoFit="always"
                />
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="depot-list">
            {loading ? (
              Array(6).fill(0).map((_, i) => (
                <Card key={i} className="bg-[#111] border-white/5 animate-pulse">
                  <CardContent className="p-5">
                    <div className="h-5 bg-white/10 rounded w-3/4 mb-3" />
                    <div className="h-4 bg-white/10 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-white/10 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))
            ) : depots.length > 0 ? (
              depots.map((depot) => (
                <Card 
                  key={depot.id} 
                  className="depot-card cursor-pointer bg-[#111] border-white/5"
                  onClick={() => handleDepotClick(depot)}
                  data-testid={`depot-card-${depot.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-serif font-semibold text-lg line-clamp-1 text-white">
                        {depot.name}
                      </h3>
                      <Badge 
                        className={`ml-2 shrink-0 ${depot.status === "original" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-blue-500/20 text-blue-400 border-blue-500/30"}`}
                      >
                        {depot.status}
                      </Badge>
                    </div>
                    
                    <p className="text-white/50 text-sm mb-3 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {depot.city}
                    </p>
                    
                    <div className="flex items-center gap-4 text-sm">
                      {depot.railroad && (
                        <span className="text-white/40 flex items-center gap-1">
                          <Train className="w-3 h-3" />
                          {depot.railroad}
                        </span>
                      )}
                      {depot.has_photo && (
                        <span className="text-amber-500 flex items-center gap-1">
                          <Camera className="w-3 h-3" />
                          Photos
                        </span>
                      )}
                    </div>

                    {depot.year_built && (
                      <p className="text-xs text-white/30 mt-2 font-mono">
                        Built: {depot.year_built}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center py-16">
                <Train className="w-16 h-16 mx-auto mb-4 text-white/10" />
                <p className="text-white/40">No depots found matching your filters</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
